import { createWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { persist, createJSONStorage } from "zustand/middleware";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Category } from "@companion-core/shared/app/Components/Bell/Categories";
import { isWeb } from "@companion-core/shared/app/Utils/platform";

export type BellContent = "home" | "categories" | "form" | "confirmation";
export type BellReferrer = "bell" | "cross-sell" | "offer" | "vendor";
type BellStore = BellState & BellActions;

interface BellState {
  bellContent: BellContent | null;
  bellCategory: null | Category;
  formPath: string | null;
  headerAccessibilityLabel: string;
  isPopUpVisible: boolean;
  isIntroPopUpVisible: boolean;
  payload: Record<string, unknown>;
  defaultBehaviour: boolean;
  vendorApiValues: Record<string, unknown>;
  bellReferrer: BellReferrer;
  fromOnboarding: boolean;
}

interface BellActions {
  setBellContent: (
    bellContent: BellContent,
    defaultBehaviour?: boolean,
    bellReferrer?: BellReferrer
  ) => void;
  setBellCategory: (bellCategory: null | Category) => void;
  setIsPopUpVisible: (isPopUpVisible: boolean) => void;
  setIsIntroPopUpVisible: (isIntroPopUpVisible: boolean) => void;
  setFormPath: (formJSON: string) => void;
  setHeaderAccessibilityLabel: (headerAccessibilityLabel: string) => void;
  setPayload: (payload: Record<string, unknown>) => void;
  setVendorApiValues: (vendorApiValues: Record<string, unknown>) => void;
  clearBell: (bellContentToSet?: BellContent) => void;
  resetBell: (introPopupState?: boolean) => void;
  setBellReferrer: (bellReferrer: BellReferrer) => void;
  setFromOnboarding: (fromOnboarding: boolean) => void;
}

export const initialState: BellState = {
  bellContent: null,
  bellCategory: null,
  formPath: null,
  headerAccessibilityLabel: "",
  isPopUpVisible: false,
  isIntroPopUpVisible: true,
  payload: {},
  defaultBehaviour: true,
  vendorApiValues: {},
  bellReferrer: "bell",
  fromOnboarding: false,
};

export const initialContentState = {
  bellContent: null,
  bellCategory: null,
  formPath: null,
  payload: {},
  defaultBehaviour: true,
  vendorApiValues: {},
  bellReferrer: "bell" as BellReferrer,
  headerAccessibilityLabel: "",
  fromOnboarding: false,
};

export const useBellStore = createWithEqualityFn<BellStore>()(
  persist(
    (set) => ({
      ...initialState,
      setBellContent: (
        bellContent: BellContent,
        defaultBehaviour?: boolean,
        bellReferrer?: BellReferrer
      ) => {
        const updates: Partial<BellState> = { bellContent };
        if (defaultBehaviour !== undefined) updates.defaultBehaviour = defaultBehaviour;
        if (bellReferrer !== undefined) updates.bellReferrer = bellReferrer;
        set(updates);
      },
      setBellCategory: (bellCategory: null | Category) => set({ bellCategory }),
      setFormPath: (formPath: string) => set({ formPath }),
      setIsPopUpVisible: (isPopUpVisible: boolean) => set({ isPopUpVisible }),
      setIsIntroPopUpVisible: (isIntroPopUpVisible: boolean) => set({ isIntroPopUpVisible }),
      setHeaderAccessibilityLabel: (headerAccessibilityLabel: string) =>
        set({ headerAccessibilityLabel }),
      setPayload: (payload: Record<string, unknown>) => set({ payload }),
      setVendorApiValues: (vendorApiValues: Record<string, unknown>) => set({ vendorApiValues }),
      clearBell: (bellContentToSet?: BellContent) =>
        set({
          ...initialContentState,
          bellContent: bellContentToSet ?? initialContentState.bellContent,
        }),
      resetBell: (introPopupState?: boolean) =>
        set({
          ...initialState,
          ...(introPopupState !== undefined && { isIntroPopUpVisible: introPopupState }),
        }),
      setBellReferrer: (bellReferrer: BellReferrer) => set({ bellReferrer }),
      setFromOnboarding: (fromOnboarding: boolean) => set({ fromOnboarding }),
    }),
    {
      name: "bell-storage",
      storage: createJSONStorage(() => (isWeb() ? localStorage : AsyncStorage)),
      partialize: (state) => {
        const stateToPersist = ["isIntroPopUpVisible"];
        return Object.fromEntries(
          Object.entries(state).filter(([key]) => stateToPersist.includes(key))
        );
      },
    }
  ),
  shallow
);
